//#region react
import React, { useMemo, useEffect } from 'react';
//#endregion

//#region redux
import { useSelector } from 'react-redux';
import { selectSelectedTaskToView } from '_redux/slices/timeline/selected-task';
import { selectAllBookingsByFlight } from '_redux/slices/timeline/timeline-bookings';
import { selectTaskList } from '_redux/slices/timeline/timeline-data';
//#endregion

//#region bootstrap
import { Card } from 'react-bootstrap';
//#endregion

//#region styles
import { Spacing } from '_styles';
import { DARK_GREEN, GRAY_DARK, GRAY_LIGHT, PRM_LIGHT_BLUE, TEXT_COLOR } from '_styles/colors';
//#endregion

//#region utils
import { getJavaScriptDate, getJobStatusColor, getOriginalBooking, processName, getAssistanceString } from '_utils/functions';

// styles
import { Colors } from '_styles';

// task colors
import * as TaskColors from '../utils/colors';

//#endregion

//#region custom components
const InfoItem = ({ label, children }) => (
    <div style={ITEM_STYLE}>
        <div style={LABEL_STYLE}>{label}</div>
        <div style={VALUE_STYLE}>{children}</div>
    </div>
);
//#endregion

const PaxJourney = ({ bookingId = '', type = 'A', flightId }) => {

    //#region local states
    // normalize booking id (strip "_IATA..." suffix if present)
    const originalBookingId = useMemo(() => {
        if (!bookingId) return '';
        return bookingId.includes('_IATA')
            ? bookingId.split('_IATA')[0]
            : bookingId;
    }, [bookingId]);
    //#endregion

    //#region redux
    const task = useSelector(selectSelectedTaskToView); // hold selected/clicked task/booking to view
    const bookings = useSelector(selectAllBookingsByFlight(flightId));
    const taskList = useSelector(selectTaskList); // all tasks on timeline
    const isTask = Boolean(task?.assignedTime);
    //#endregion

    //#region Step 1: Get original booking
    const original = useMemo(
        () => getOriginalBooking(bookings, originalBookingId) ?? {},
        [bookings, originalBookingId]
    );
    //#endregion

    //#region Step 2: Get tasks for this pax from task list
    const paxTasks = useMemo(() => {
        if (!taskList || !originalBookingId) return [];
        return taskList.filter(t => 
            // Match preboard tasks by _id prefix
            t?._id?.startsWith(originalBookingId) || 
            // Match boarding tasks by passengers array (combined boarding tasks)
            t?.passengers?.some(p => p?.originalBookingId === originalBookingId)
        ) ?? [];
    }, [taskList, originalBookingId]);

    // Preboard task (isBoardingBooking: false)
    const preboardTask = useMemo(() => {
        return paxTasks.find(t => !t?.isBoardingBooking) ?? null;
    }, [paxTasks]);

    // Boarding task (isBoardingBooking: true)
    const boardingTask = useMemo(() => {
        return paxTasks.find(t => t?.isBoardingBooking) ?? null;
    }, [paxTasks]);
    //#endregion

    //#region Step 3: Get check-in info from booking history
    const checkInInfo = useMemo(() => {
        const history = original?.paxPositionHistory ?? [];
        return history.find(event => event?.activity === 'Checked-in') ?? null;
    }, [original]);
    //#endregion

    //#region Step 4: Derive display values

    // Helper function to format time
    const formatTime = (date) => {
        if (!date) return null;
        const jsDate = getJavaScriptDate(date);
        return jsDate.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // Header styles
    const headerBgColor = useMemo(
        () => (isTask ? getJobStatusColor(task) : type === 'A' ? DARK_GREEN : PRM_LIGHT_BLUE),
        [isTask, task, type]
    );

    const headerStyle = useMemo(
        () => ({ ...BASE_HEADER_STYLE, backgroundColor: headerBgColor }),
        [headerBgColor]
    );

    // Check-in derived values
    const checkInTime = useMemo(() => {
        if (!checkInInfo?.time) return null;
        return new Date(checkInInfo.time).toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
        });
    }, [checkInInfo]);

    const waitingTimeDisplay = useMemo(() => {
        const waitingData = original?.departureWaitingTime;
        if (!waitingData?.startWaitingTime || !waitingData?.atLocationTime) return null;
        
        const diffMs = new Date(waitingData.atLocationTime) - new Date(waitingData.startWaitingTime);
        if (isNaN(diffMs) || diffMs < 0) return null;
        
        const minutes = Math.floor(diffMs / 60000);
        const seconds = Math.floor((diffMs % 60000) / 1000);
        return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
    }, [original]);

    // Preboard derived values - derived from task if it exists
    const preboardInfo = useMemo(() => {
        // If no preboard task exists, return null
        if (!preboardTask) return null;

        return {
            allocated: !!preboardTask?.allocatedTime,  // true if task has been allocated to an agent
            active: preboardTask?.active ?? false,
            completed: preboardTask?.completed ?? false,
            acknowledged: preboardTask?.acknowledged ?? false,
            isAtLocation: preboardTask?.isAtLocation ?? false,
            started: !!preboardTask?.startTime,
            startTime: preboardTask?.startTime ?? null,
            completeTime: preboardTask?.completeTime ?? null,
            allocatedTime: preboardTask?.allocatedTime ?? null,
            acknowledgedTime: preboardTask?.acknowledgedTime ?? null,
            atLocationTime: preboardTask?.atLocationTime ?? null,
            pickUpLocation: preboardTask?.pickUpLocation ?? null,
            dropOffLocation: preboardTask?.dropOffLocation ?? null,
            employeeName: preboardTask?.employeeName ?? null,
            employeeId: preboardTask?.employeeId ?? null,
            assignedTime: preboardTask?.assignedTime ?? null,
        };
    }, [preboardTask]);

    const preboardStartTime = useMemo(() => formatTime(preboardInfo?.startTime), [preboardInfo]);
    const preboardCompletedTime = useMemo(() => formatTime(preboardInfo?.completeTime), [preboardInfo]);

    // Boarding derived values
    // Priority: 1) booking's departureJourney boarding entry, 2) boarding task data
    // IMPORTANT: If passenger is a no-show, boarding cannot be completed!
    const boardingInfo = useMemo(() => {
        // Check if passenger is a no-show - they cannot have completed boarding
        const isNoShow = original?.noShow === true;

        // First check booking's departureJourney for boarding entry (primary source)
        // This is the ONLY reliable source for boarding completion
        const boardingJourney = original?.departureJourney?.find(j => j?.journey === 'boarding');
        
        // If we have boarding journey data from the booking, use it as primary source
        if (boardingJourney) {
            // Only mark as completed if selectPaxTime exists AND not a no-show
            const actuallyCompleted = !isNoShow && !!boardingJourney?.selectPaxTime;
            
            return {
                allocated: !!boardingJourney?.allocatedTime,
                active: boardingTask?.active ?? false,
                taskCompleted: boardingTask?.completed ?? false,
                completed: actuallyCompleted,  // passenger boarded if selectPaxTime exists AND not no-show
                acknowledged: !!boardingJourney?.acknowledgedTime,
                isAtLocation: !!boardingJourney?.atLocationTime,
                boardingStarted: !!boardingJourney?.boardingStartTime || !!boardingJourney?.startTime,
                started: !!boardingJourney?.startTime,
                startTime: boardingJourney?.startTime ?? boardingJourney?.boardingStartTime ?? null,
                completeTime: actuallyCompleted ? (boardingJourney?.selectPaxTime ?? null) : null,
                journeyCompleteTime: boardingJourney?.completeTime ?? null,
                taskCompleteTime: boardingTask?.completeTime ?? null,
                allocatedTime: boardingJourney?.allocatedTime ?? null,
                acknowledgedTime: boardingJourney?.acknowledgedTime ?? null,
                atLocationTime: boardingJourney?.atLocationTime ?? null,
                pickUpLocation: boardingJourney?.pickUpLocation ?? null,
                dropOffLocation: boardingJourney?.dropOffLocation ?? null,
                employeeName: boardingJourney?.employeeName ?? null,
                employeeId: boardingJourney?.employeeId ?? boardingTask?.employeeId ?? null,
                assignedTime: boardingTask?.assignedTime ?? null,
                taskId: boardingJourney?.taskId ?? null,
                isNoShow: isNoShow,
            };
        }

        // No departureJourney boarding entry means boarding is NOT completed
        // Even if task says completed or selectBoardingPaxStatus says boarded - 
        // without departureJourney entry, it didn't actually happen

        // Fallback to boarding task data for allocation/active status only
        if (!boardingTask) return null;

        // Find this specific passenger in the passengers array
        const passengerEntry = boardingTask?.passengers?.find(
            p => p?.originalBookingId === originalBookingId
        );

        // Only mark as completed if:
        // 1. Not a no-show
        // 2. Has selectPaxTime in task passengers array
        // 3. OR has departureJourney boarding entry (handled above)
        const paxBoarded = !isNoShow && !!passengerEntry?.selectPaxTime;
        const paxBoardedTime = paxBoarded ? (passengerEntry?.selectPaxTime ?? null) : null;

        return {
            allocated: !!boardingTask?.allocatedTime,
            active: boardingTask?.active ?? false,
            taskCompleted: boardingTask?.completed ?? false,
            completed: paxBoarded,
            acknowledged: boardingTask?.acknowledged ?? false,
            isAtLocation: boardingTask?.isAtLocation ?? false,
            boardingStarted: boardingTask?.boardingStarted ?? false,
            started: !!boardingTask?.startTime,
            startTime: boardingTask?.startTime ?? boardingTask?.boardingStartTime ?? null,
            completeTime: paxBoardedTime,
            taskCompleteTime: boardingTask?.completeTime ?? null,
            allocatedTime: boardingTask?.allocatedTime ?? null,
            acknowledgedTime: boardingTask?.acknowledgedTime ?? null,
            atLocationTime: boardingTask?.atLocationTime ?? null,
            pickUpLocation: boardingTask?.pickUpLocation ?? null,
            dropOffLocation: boardingTask?.dropOffLocation ?? null,
            employeeName: boardingTask?.employeeName ?? null,
            employeeId: boardingTask?.employeeId ?? null,
            assignedTime: boardingTask?.assignedTime ?? null,
            passengerEntry: passengerEntry,
            isNoShow: isNoShow,
        };
    }, [original, boardingTask, originalBookingId]);

    const boardingStartTime = useMemo(() => formatTime(boardingInfo?.startTime), [boardingInfo]);
    const boardingCompletedTime = useMemo(() => formatTime(boardingInfo?.completeTime), [boardingInfo]);
    //#endregion

    //#region debug effects
    useEffect(() => {
        console.log("========== PAX JOURNEY DEBUG ==========");
        console.log("Booking ID:", originalBookingId);
    }, [originalBookingId]);

    useEffect(() => {
        console.log("BOOKING:", original);
    }, [original]);

    useEffect(() => {
        console.log("ALL PAX TASKS:", paxTasks);
    }, [paxTasks]);

    useEffect(() => {
        console.log("PREBOARD TASK:", preboardTask);
    }, [preboardTask]);

    useEffect(() => {
        console.log("BOARDING TASK:", boardingTask);
    }, [boardingTask]);

    useEffect(() => {
        console.log("CHECK-IN INFO:", checkInInfo);
    }, [checkInInfo]);

    //#endregion

    //#region render
    return (
        <>

            {/* passenger information tab */}
            <Card style={CARD_STYLE}>
                
                {/* header */}
                <Card.Header style={{ backgroundColor: type === 'A' ? DARK_GREEN : PRM_LIGHT_BLUE, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p style={{ margin: 0, color: 'white', fontSize: 16 }}>{'Passenger'}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {original?.selfAssisted && (
                            <span style={{ backgroundColor: '#8b5cf6', color: 'white', fontSize: 11, fontWeight: 'bold', padding: '2px 8px', borderRadius: 12 }}>SELF ASSISTED</span>
                        )}
                        {original?.noShow && (
                            <span style={{ backgroundColor: '#ef4444', color: 'white', fontSize: 11, fontWeight: 'bold', padding: '2px 8px', borderRadius: 12 }}>NO SHOW</span>
                        )}
                    </div>
                </Card.Header>
                
                {/* body */}
                <Card.Body style={CONTENT_STYLE}>
                    <div style={ROW_STYLE}>
                        <InfoItem label="Name">{original?.name ? processName(original.name) : 'Not Set'}</InfoItem>
                        <InfoItem label="Assistance">{getAssistanceString(original?.assistance) ?? 'Not Set'}</InfoItem>
                        {/* <InfoItem label="Assistance Required">{original?.selfAssisted ? 'Self Assisted' : original?.noShow ? 'No Show' : 'Assistance Required'}</InfoItem> */}
                        <InfoItem label="Notes">{original?.notes ?? 'Not Set'}</InfoItem>
                        <InfoItem label="">{''}</InfoItem>
                    </div>
                </Card.Body>
            </Card>
            

            {/* check in tab */}
            <Card style={{ ...CARD_STYLE, opacity: checkInInfo ? 1 : 0.4 }}>
                
                {/* header */}
                <Card.Header style={{ backgroundColor:Colors.PRM_DARK_BLUE, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p style={{ margin: 0, color: 'white', fontSize: 16 }}>{'Check-In'}</p>
                    {checkInInfo && <span style={{ backgroundColor: '#22c55e', color: 'white', fontSize: 12, fontWeight: 'bold', width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</span>}
                </Card.Header>

                {/* body */}    
                <Card.Body style={CONTENT_STYLE}>
                    <div style={ROW_STYLE}>
                        <InfoItem label="Checked In">{checkInInfo ? 'Yes' : 'No'}</InfoItem>
                        <InfoItem label="Time">{checkInTime ?? 'Not Set'}</InfoItem>
                        <InfoItem label="Location">{checkInInfo?.locationTitle ?? 'Not Set'}</InfoItem>
                        <InfoItem label="Waiting Time">{waitingTimeDisplay ?? 'Not Set'}</InfoItem>
                    </div>
                </Card.Body>

            </Card>

            {/* preboard tab */}
            <Card style={CARD_STYLE}>
                {/* header */}
                <Card.Header style={{ backgroundColor:Colors.PRM_LIGHT_BLUE, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p style={{ margin: 0, color: 'white', fontSize: 16 }}>{'Preboard'}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {original?.selfAssisted && (
                            <span style={{ backgroundColor: '#8b5cf6', color: 'white', fontSize: 11, fontWeight: 'bold', padding: '2px 8px', borderRadius: 12 }}>SELF ASSISTED</span>
                        )}
                        {preboardInfo?.active && !preboardInfo?.completed && !original?.selfAssisted && (
                            <span style={{ backgroundColor: '#f59e0b', color: 'white', fontSize: 11, fontWeight: 'bold', padding: '2px 8px', borderRadius: 12 }}>ACTIVE</span>
                        )}
                        {preboardInfo?.completed && !original?.selfAssisted && <span style={{ backgroundColor: '#22c55e', color: 'white', fontSize: 12, fontWeight: 'bold', width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</span>}
                    </div>
                </Card.Header>
                {/* body */}
                <Card.Body style={CONTENT_STYLE}>
                    <div style={ROW_STYLE}>
                        <InfoItem label="Preboard">{original?.selfAssisted ? 'Self Assisted' : preboardInfo ? 'Yes' : 'Not Set'}</InfoItem>
                        <InfoItem label="Started">{original?.selfAssisted ? 'Not Set' : preboardStartTime ?? 'Not Set'}</InfoItem>
                        <InfoItem label="Completed">{original?.selfAssisted ? 'No' : preboardInfo?.completed ? (preboardCompletedTime ?? 'Yes') : 'No'}</InfoItem>
                        <InfoItem label="Pickup">{preboardInfo?.pickUpLocation?.name ?? 'Not Set'}</InfoItem>
                        <InfoItem label="Drop">{original?.boardingPickupLocation?.name ?? preboardInfo?.dropOffLocation?.name ?? 'Not Set'}</InfoItem>
                        <InfoItem label="Agent">{preboardInfo?.employeeName ?? preboardInfo?.employeeId ?? 'Not Set'}</InfoItem>
                    </div>
                </Card.Body>
            </Card>

            {/* boarding tab */}
            <Card style={{ ...CARD_STYLE, opacity: boardingInfo ? 1 : 0.4 }}>
                {/* header */}
                <Card.Header style={{ backgroundColor:TaskColors.combinedBooking, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p style={{ margin: 0, color: 'white', fontSize: 16 }}>{'Boarding'}</p>
                    {boardingInfo?.completed && <span style={{ backgroundColor: '#22c55e', color: 'white', fontSize: 12, fontWeight: 'bold', width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</span>}
                </Card.Header>
                {/* body */}
                <Card.Body style={CONTENT_STYLE}>
                    <div style={ROW_STYLE}>
                        <InfoItem label="Boarding">{boardingInfo ? 'Yes' : 'Not Set'}</InfoItem>
                        <InfoItem label="Started">{boardingStartTime ?? 'Not Set'}</InfoItem>
                        <InfoItem label="Completed">{boardingInfo?.completed ? (boardingCompletedTime ?? 'Yes') : 'No'}</InfoItem>
                        <InfoItem label="Pickup">{boardingInfo?.pickUpLocation?.name ?? 'Not Set'}</InfoItem>
                        <InfoItem label="Drop">{boardingInfo?.dropOffLocation?.name ?? 'Not Set'}</InfoItem>
                        <InfoItem label="Agent">{boardingInfo?.employeeName ?? boardingInfo?.employeeId ?? 'Not Set'}</InfoItem>
                    </div>
                </Card.Body>
            </Card>

            {/* arrival tab (only show if type is A)*/}
            {type === 'A' && (
            <Card style={CARD_STYLE}>
                {/* header */}
                <Card.Header style={{ backgroundColor:Colors.LIGHT_GREEN }}>
                    <p style={{ margin: 0, color: 'white', fontSize: 16 }}>{'Arrival'}</p>
                </Card.Header>
            </Card>
            )}

        </>
    );
    //#endregion
};

export default PaxJourney;

//#region styles
const CARD_STYLE = {
    marginBottom: Spacing.SCALE_16,
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    backgroundColor: 'white',
};

const BASE_HEADER_STYLE = {
    color: 'white',
    padding: Spacing.SCALE_12,
    borderRadius: '8px 8px 0 0',
    margin: 0,
    display: 'flex',
    justifyContent: 'space-between',
};

const EMPTY_STATE_STYLE = {
    textAlign: 'center',
    padding: Spacing.SCALE_32,
    color: GRAY_DARK,
    backgroundColor: GRAY_LIGHT,
    borderRadius: '8px',
    border: `1px solid ${GRAY_LIGHT}`,
};

const CONTAINER_STYLE = { marginBottom: Spacing.SCALE_24 };

const ROW_STYLE = {
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'flex-start',
    gap: Spacing.SCALE_16,
};

const ITEM_STYLE = {
    flex: 1,
    textAlign: 'flex-start',
    backgroundColor: 'transparent',
    border: '2px solid transparent',
    borderRadius: '6px',
    padding: 0,
    transition: 'all 0.2s ease',
};

const LABEL_STYLE = {
    fontSize: 12,
    fontWeight: 600,
    color: GRAY_DARK,
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
};

const VALUE_STYLE = {
    fontSize: 14,
    color: TEXT_COLOR,
    margin: 0,
    fontWeight: 500,
};

const CONTENT_STYLE = { padding: Spacing.SCALE_16 };
//#endregion